import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";

interface OnboardingData {
  date: string;
  generate_metrics_clicks: number;
  metrics_generated: number;
  sign_in_clicks: number;
  setup_later_clicks: number;
  captcha_failures: number;
  email_signups: number;
  google_signups: number;
  apple_signups: number;
  referral_codes_used: number;
  usernames_created: number;
}

type MetricType = 
  | "generate_metrics_clicks"
  | "metrics_generated"
  | "sign_in_clicks"
  | "setup_later_clicks"
  | "captcha_failures"
  | "signup_methods"
  | "referral_codes_used"
  | "usernames_created";

const AdminOnboardingAnalytics = () => {
  const { t } = useTranslation();

  const [metric, setMetric] = useState<MetricType>("generate_metrics_clicks");
  const [data, setData] = useState<OnboardingData[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<"30" | "60" | "90">("30");

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);

      const days = parseInt(timeRange);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Call the database function to get analytics (uses admin-safe version with role check)
      // Using 'any' cast because the RPC function is newly created
      const { data: analyticsData, error } = await (supabase.rpc as any)("get_onboarding_analytics_for_admin", {
        p_start_date: startDate.toISOString(),
        p_end_date: new Date().toISOString(),
      });

      if (error) throw error;

      // Transform data to include all fields
      const transformedData: OnboardingData[] = (Array.isArray(analyticsData) ? analyticsData : []).map((row: any) => ({
        date: row.date,
        generate_metrics_clicks: Number(row.generate_metrics_clicks) || 0,
        metrics_generated: Number(row.metrics_generated) || 0,
        sign_in_clicks: Number(row.sign_in_clicks) || 0,
        setup_later_clicks: Number(row.setup_later_clicks) || 0,
        captcha_failures: Number(row.captcha_failures) || 0,
        email_signups: Number(row.email_signups) || 0,
        google_signups: Number(row.google_signups) || 0,
        apple_signups: Number(row.apple_signups) || 0,
        referral_codes_used: Number(row.referral_codes_used) || 0,
        usernames_created: Number(row.usernames_created) || 0,
      }));

      setData(transformedData);
    } catch (error) {
      console.error("[AdminOnboardingAnalytics] Error fetching data:", error);
      toast.error(t('notifications.toast.failed_to_load_onboarding_analytics_data'));
    } finally {
      setLoading(false);
    }
  };

  const getMetricLabel = () => {
    switch (metric) {
      case "generate_metrics_clicks":
        return '"Generate metrics" Button Clicks';
      case "metrics_generated":
        return "Metrics Successfully Generated";
      case "sign_in_clicks":
        return '"Sign in" Button Clicks';
      case "setup_later_clicks":
        return '"Set up later" Button Clicks';
      case "captcha_failures":
        return "Captcha Failures";
      case "signup_methods":
        return "Signups by Method";
      case "referral_codes_used":
        return "Referral Codes Used";
      case "usernames_created":
        return "Usernames Created";
      default:
        return "Metric";
    }
  };

  const getCurrentValue = () => {
    if (data.length === 0) return 0;
    const latest = data[data.length - 1];
    
    switch (metric) {
      case "generate_metrics_clicks":
        return latest.generate_metrics_clicks;
      case "metrics_generated":
        return latest.metrics_generated;
      case "sign_in_clicks":
        return latest.sign_in_clicks;
      case "setup_later_clicks":
        return latest.setup_later_clicks;
      case "captcha_failures":
        return latest.captcha_failures;
      case "signup_methods":
        return latest.email_signups + latest.google_signups + latest.apple_signups;
      case "referral_codes_used":
        return latest.referral_codes_used;
      case "usernames_created":
        return latest.usernames_created;
      default:
        return 0;
    }
  };

  const getTotalValue = () => {
    return data.reduce((sum, row) => {
      switch (metric) {
        case "generate_metrics_clicks":
          return sum + row.generate_metrics_clicks;
        case "metrics_generated":
          return sum + row.metrics_generated;
        case "sign_in_clicks":
          return sum + row.sign_in_clicks;
        case "setup_later_clicks":
          return sum + row.setup_later_clicks;
        case "captcha_failures":
          return sum + row.captcha_failures;
        case "signup_methods":
          return sum + row.email_signups + row.google_signups + row.apple_signups;
        case "referral_codes_used":
          return sum + row.referral_codes_used;
        case "usernames_created":
          return sum + row.usernames_created;
        default:
          return sum;
      }
    }, 0);
  };

  const renderChart = () => {
    if (metric === "signup_methods") {
      // Show stacked chart for signup methods
      return (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="date" 
              className="text-xs"
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              className="text-xs"
              tick={{ fontSize: 12 }}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="email_signups" 
              stroke="#3b82f6" 
              strokeWidth={2}
              name="Email"
              dot={{ fill: '#3b82f6', r: 3 }}
            />
            <Line 
              type="monotone" 
              dataKey="google_signups" 
              stroke="#ef4444" 
              strokeWidth={2}
              name="Google"
              dot={{ fill: '#ef4444', r: 3 }}
            />
            <Line 
              type="monotone" 
              dataKey="apple_signups" 
              stroke="#000000" 
              strokeWidth={2}
              name="Apple"
              dot={{ fill: '#000000', r: 3 }}
            />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    // Single line chart for other metrics
    const dataKey = metric;
    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis 
            dataKey="date" 
            className="text-xs"
            tick={{ fontSize: 12 }}
          />
          <YAxis 
            className="text-xs"
            tick={{ fontSize: 12 }}
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: 'hsl(var(--background))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '8px',
            }}
          />
          <Line 
            type="monotone" 
            dataKey={dataKey} 
            stroke="hsl(var(--primary))" 
            strokeWidth={2}
            dot={{ fill: 'hsl(var(--primary))', r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <CardTitle>{t('ui.text.onboarding_analytics')}</CardTitle>
          <div className="flex gap-2">
            <Select value={timeRange} onValueChange={(value: any) => setTimeRange(value)}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder={t('forms.placeholders.time_range')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">{t('ui.text.last_30_days')}</SelectItem>
                <SelectItem value="60">{t('ui.text.last_60_days')}</SelectItem>
                <SelectItem value="90">{t('ui.text.last_90_days')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={metric} onValueChange={(value) => setMetric(value as MetricType)}>
              <SelectTrigger className="w-[280px]">
                <SelectValue placeholder={t('forms.placeholders.select_metric')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="generate_metrics_clicks">{t('ui.text.generate_metrics_clicks')}</SelectItem>
                <SelectItem value="metrics_generated">{t('ui.text.metrics_generated')}</SelectItem>
                <SelectItem value="sign_in_clicks">{t('ui.text.sign_in_clicks')}</SelectItem>
                <SelectItem value="setup_later_clicks">{t('ui.text.set_up_later_clicks')}</SelectItem>
                <SelectItem value="captcha_failures">{t('ui.text.captcha_failures')}</SelectItem>
                <SelectItem value="signup_methods">{t('ui.text.signup_methods')}</SelectItem>
                <SelectItem value="referral_codes_used">{t('ui.text.referral_codes_used')}</SelectItem>
                <SelectItem value="usernames_created">{t('ui.text.usernames_created')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-[300px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">{t('ui.text.no_data_available_for_the_selected_time_range')}</div>
        ) : (
          <div>
            <div className="mb-4 grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">{getMetricLabel()}</p>
                <p className="text-3xl font-bold">{getTotalValue()}</p>
                <p className="text-xs text-muted-foreground mt-1">{t('ui.text.total_in_period')}</p>
              </div>
              {metric !== "signup_methods" && (
                <div>
                  <p className="text-sm text-muted-foreground">{t('ui.text.latest_day')}</p>
                  <p className="text-3xl font-bold">{getCurrentValue()}</p>
                  <p className="text-xs text-muted-foreground mt-1">{data[data.length - 1]?.date}</p>
                </div>
              )}
            </div>
            {renderChart()}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminOnboardingAnalytics;
